import { useInfiniteQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Controls, ControlsButton } from '@/components/Controls';
import DateRangeFilter from '@/components/DateRangeFilter';
import ExportButton from '@/components/ExportButton';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, SectionHeading } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { SortableTh, Table, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import useSortState from '@/hooks/useSortState';
import { IgPost, IgProfile, PaginatedResponse } from '@/types/resources';
import { api } from '@/utils/api';

import IgProfilesFilter from './components/IgProfilesFilter';
import PostTypeFilter from './components/PostTypeFilter';
import TableRow from './components/TableRow';

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 16px;

  ${SectionHeading} {
    margin: 0;
  }
`;

const StyledTable = styled(Table)`
  thead {
    th {
      vertical-align: top;
    }
  }
  th,
  td {
    padding: 16px 24px;
    vertical-align: center;

    &:first-child {
      padding-left: 24px;
      width: 400px;
    }
    /* &:nth-child(2) {
      width: 120px;
    }
    &:nth-child(3) {
      width: 120px;
    }
    &:nth-child(4) {
      width: 100px;
    }
    &:nth-child(5) {
      width: 300px;
    }
    &:nth-child(6) {
      width: 120px;
    }
    &:nth-child(7) {
      width: 120px;
    }
    &:nth-child(8) {
      width: 120px;
    }
    &:nth-child(9) {
      width: 120px;
    }
    &:nth-child(10) {
      width: 120px;
    }
    &:nth-child(11) {
      width: 140px;
    }
    &:nth-child(12) {
      width: 140px;
    } */
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const FiltersHolder = styled.div`
  display: flex;
  gap: 8px;
`;

enum DatePresets {
  LAST_24H = 1,
  LAST_3D = 2,
  LAST_7D = 3,
}

const IgPosts = () => {
  const { sort, setSort, sortQueryParamObj } = useSortState();
  const [activeType, setActiveType] = useState('');
  const [activeProfiles, setActiveProfiles] = useState<IgProfile[]>([]);
  const [activeDatePreset, setActiveDatePreset] = useState<DatePresets | null>(
    null,
  );

  const [selectedDateRange, setSelectedDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});

  const fetchPosts = ({ pageParam = '/v1/ig-profiles-tracker/posts' }) => {
    return api.get<never, PaginatedResponse<IgPost>>(pageParam, {
      params: {
        ...sortQueryParamObj,
        ...(!!activeType && {
          'filter[type]': activeType,
        }),
        ...(!!activeProfiles.length && {
          'filter[igProfileId]': activeProfiles.map((p) => p.id).join(','),
        }),
        ...(selectedDateRange.rangeStart &&
          selectedDateRange.rangeEnd && {
            'filter[dateFrom]': selectedDateRange.rangeStart.toString(),
            'filter[dateTo]': selectedDateRange.rangeEnd
              .plus({ days: 1 })
              .toString(),
          }),
        ...(selectedDateRange.rangeEnd &&
          !selectedDateRange.rangeStart && {
            'filter[dateFrom]': selectedDateRange.rangeEnd.toString(),
            'filter[dateTo]': selectedDateRange.rangeEnd
              .plus({ days: 1 })
              .toString(),
          }),
        ...(!selectedDateRange.rangeEnd &&
          selectedDateRange.rangeStart && {
            'filter[dateFrom]': selectedDateRange.rangeStart.toString(),
            'filter[dateTo]': selectedDateRange.rangeStart
              .plus({ days: 1 })
              .toString(),
          }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [
      'ig_trackable_posts',
      {
        sort,
        selectedDateRange,
        activeType,
        activeProfiles: activeProfiles.map((p) => p.id).join(','),
      },
    ],
    fetchPosts,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const posts = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<IgPost[]>((acc, currKey: any) => {
        return [...acc, ...data!.pages[currKey].data];
      }, []),
    [data],
  );

  const onExport = () =>
    api.get<never, string>(`/v1/ig-profiles-tracker/posts`, {
      params: {
        format: 'csv',
        ...sortQueryParamObj,
        ...(!!activeType && {
          'filter[type]': activeType,
        }),
        ...(selectedDateRange.rangeStart &&
          selectedDateRange.rangeEnd && {
            'filter[dateFrom]': selectedDateRange.rangeStart.toString(),
            'filter[dateTo]': selectedDateRange.rangeEnd
              .plus({ days: 1 })
              .toString(),
          }),
        ...(selectedDateRange.rangeEnd &&
          !selectedDateRange.rangeStart && {
            'filter[dateFrom]': selectedDateRange.rangeEnd.toString(),
            'filter[dateTo]': selectedDateRange.rangeEnd
              .plus({ days: 1 })
              .toString(),
          }),
        ...(!selectedDateRange.rangeEnd &&
          selectedDateRange.rangeStart && {
            'filter[dateFrom]': selectedDateRange.rangeStart.toString(),
            'filter[dateTo]': selectedDateRange.rangeStart
              .plus({ days: 1 })
              .toString(),
          }),
      },
    });

  const onDatePresetClick = (preset: DatePresets) => {
    switch (preset) {
      case DatePresets.LAST_24H:
        setSelectedDateRange({
          rangeStart: DateTime.now().minus({ days: 1 }),
          rangeEnd: DateTime.now(),
        });
        break;
      case DatePresets.LAST_3D:
        setSelectedDateRange({
          rangeStart: DateTime.now().minus({ days: 3 }).startOf('day'),
          rangeEnd: DateTime.now(),
        });
        break;
      case DatePresets.LAST_7D:
        setSelectedDateRange({
          rangeStart: DateTime.now().minus({ days: 7 }).startOf('day'),
          rangeEnd: DateTime.now(),
        });
        break;
    }

    setActiveDatePreset(preset);
  };

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>

      <PageHeading>Instagram Posts</PageHeading>
      <TableHeader>
        <SectionHeading>Posts List</SectionHeading>
        <FiltersHolder>
          <Controls>
            <ControlsButton
              active={activeDatePreset === DatePresets.LAST_24H}
              onClick={() => onDatePresetClick(DatePresets.LAST_24H)}
            >
              24 hours
            </ControlsButton>
            <ControlsButton
              active={activeDatePreset === DatePresets.LAST_3D}
              onClick={() => onDatePresetClick(DatePresets.LAST_3D)}
            >
              3 days
            </ControlsButton>
            <ControlsButton
              active={activeDatePreset === DatePresets.LAST_7D}
              onClick={() => onDatePresetClick(DatePresets.LAST_7D)}
            >
              7 days
            </ControlsButton>
          </Controls>
          <ExportButton fileName="ig-posts.csv" fetchCsv={onExport} />
          <IgProfilesFilter
            activeProfiles={activeProfiles}
            setActiveProfiles={setActiveProfiles}
          />
          <PostTypeFilter
            activeType={activeType}
            setActiveType={setActiveType}
          />
          <DateRangeFilter
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={(range) => {
              setSelectedDateRange(range);
              setActiveDatePreset(null);
            }}
            text="Published At"
            allDaysSelectable
          />
          <FullScreenButton />
        </FiltersHolder>
      </TableHeader>
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Thumbnail</Th>
              <SortableTh
                title="Likes %"
                property="likesAvgRatio"
                sort={sort}
                setSort={setSort}
              />
              <SortableTh
                title="Likes"
                property="likes"
                sort={sort}
                setSort={setSort}
              />
              {/* <Th>Type</Th> */}
              {/* <Th>Caption</Th> */}
              <SortableTh
                title="Comments"
                property="comments"
                sort={sort}
                setSort={setSort}
              />
              {/* <SortableTh
                title="Comments %"
                subtitle="Last 30 days"
                property="commentsPercentile"
                sort={sort}
                setSort={setSort}
              /> */}
              {/* <SortableTh
                title="Views"
                subtitle="Only for videos"
                property="views"
                sort={sort}
                setSort={setSort}
              />
              <SortableTh
                title="Views %"
                subtitle="Last 30 days"
                property="viewsPercentile"
                sort={sort}
                setSort={setSort}
              />
              <Th>Username</Th> */}
              <SortableTh
                title="Published At"
                property="publishedAt"
                sort={sort}
                setSort={setSort}
              />
              {/* <SortableTh
                title="Updated At"
                property="updatedAt"
                sort={sort}
                setSort={setSort}
              /> */}
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!posts?.length}
              colSpan={5}
            >
              {posts?.map((post) => <TableRow key={post.id} post={post} />)}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default IgPosts;
